import React from 'react';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { GET_SALES_IN_TIME_SCALE } from '../../../graphql/queries';
import { Multimedia, TChartFilters } from '../../../models';
import { useAdminDashboard } from '../../../hooks';
import KpisInfo from '../SimpleDashboard/Kpis/KpisInfo/KpisInfo';
import LeaderBoard from '../LeaderBoard/LeaderBoard';
import LineChartContainer from '../Charts/LineChart/LineChartContainer';
import DoughnutChart from '../Charts/DouhnutChart/DoughnutChart';
import ChartFilters from '../Charts/ChartFilters';

interface AdminDashboardProps {
  allyId?: string;
}

function AdminDashboard({ allyId }: AdminDashboardProps) {
  const [generalFilters, setGeneralFilters] = React.useState({
    timeScale: 'day',
    // last month
    startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    // today
    endDate: dayjs().format('YYYY-MM-DD'),

    ...(allyId !== '' ? { allyId } : {}),
  });

  const dashboardData = useAdminDashboard({
    ...generalFilters,
  });

  const [lineChartFilters, setLineChartFilters] = React.useState<TChartFilters>(
    {
      timeScale: 'day',
      // last month
      startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      // today
      endDate: dayjs().format('YYYY-MM-DD'),

      ...(allyId !== '' ? { allyId } : {}),
    }
  );

  const { data, loading, error } = useQuery<{
    getSalesInTimeScale: { timeScales: JSON[] };
  }>(GET_SALES_IN_TIME_SCALE, {
    variables: {
      data: {
        endDate: lineChartFilters.endDate,
        startDate: lineChartFilters.startDate,
        timeScale: lineChartFilters.timeScale,
        ...(lineChartFilters.allyId !== '' ? { allyId } : {}),
      },
    },
    fetchPolicy: 'network-only',
  });

  const leaderBoardData = React.useMemo(() => {
    if (allyId !== '') {
      const __data = dashboardData?.periodTopProducts?.data?.map((item) => ({
        amount: item.amount,
        name: `${item?.product.title}`,
        photoSrc: item?.product?.variants[0]?.photos[0]?.src ?? '',
      }));
      return __data.slice(0, 7);
    }

    const _data = dashboardData.periodAllySales?.data?.map((item) => ({
      amount: item.sales,
      name: `${item?.company.name}`,
      photoSrc: (item?.company.logo as Multimedia)?.src ?? '',
    }));
    return _data.slice(0, 7);
  }, [dashboardData?.periodAllySales, dashboardData?.periodTopProducts]);

  const doughnutData = React.useMemo(() => {
    const _data = dashboardData?.periodCategorySales?.data?.map(
      (item) => item.sales
    );

    const _labels = dashboardData?.periodCategorySales?.data?.map(
      (item) => item.category.name
    );

    return {
      data: _data,
      labels: _labels,
    };
  }, [dashboardData?.periodCategorySales]);

  return (
    <div className="w-full flex flex-col flex-wrap space-y-5">
      <ChartFilters
        chartFilters={generalFilters}
        setChartFilters={setGeneralFilters}
        loading={false}
        timeOptions={['day', 'month']}
      />
      <KpisInfo
        kpiItems={dashboardData?.kpisInfo?.kpis ?? []}
        loading={dashboardData?.kpisInfo?.loading}
      />
      <div className="flex flex-col lg:flex-row w-full lg:space-x-5 space-y-5 lg:space-y-0 ">
        <div className="w-full lg:w-2/3">
          <DoughnutChart
            data={doughnutData?.data}
            loading={dashboardData?.periodCategorySales?.loading}
            title="Ventas por categoría"
            labels={doughnutData?.labels}
          />
        </div>
        <div className="w-full lg:w-1/3">
          <LeaderBoard
            data={leaderBoardData}
            loading={dashboardData.periodAllySales?.loading}
            title={allyId !== '' ? 'Productos más vendidos' : 'Top aliados'}
            amountType={allyId !== '' ? 'number' : 'money'}
          />
        </div>
      </div>
      <div className="w-full ">
        <LineChartContainer
          data={data?.getSalesInTimeScale?.timeScales}
          title="Ventas"
          loading={loading}
          lineChartFilters={lineChartFilters}
          setLineChartFilters={setLineChartFilters}
        />
      </div>
    </div>
  );
}

export default AdminDashboard;
